import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Search, ChevronRight } from 'lucide-react'
import { useAppSelector } from '@/store/hooks'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import { HistoryStaffWorkloadService } from '@/api/generated/services/HistoryStaffWorkloadService'
import type { StaffWorkloadResponse } from '@/api/generated/models/StaffWorkloadResponse'
import type { ColDef, GetRowIdParams, ICellRendererParams } from 'ag-grid-community'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR'
import StaffWorkloadDetailPanel from './components/StaffWorkloadDetailPanel'

/**
 * 직원 업무 보유 현황 (데이터센터)
 *
 * 워크스페이스 소속 멤버 1명당 1행. 집계는 전부 서버(/api/history/staff-workload)가 하고,
 * 이 화면은 검색·페이지 누적 로딩·표 렌더만 담당한다.
 * 상세 "보기" 는 사이드패널(StaffWorkloadDetailPanel)을 연다.
 */

const NUMBER_COLUMNS: Array<{ field: keyof StaffWorkloadResponse; headerName: string; width: number }> = [
  { field: 'business_count', headerName: '사업(건)', width: 100 },
  { field: 'task_group_count', headerName: '전략과제(건)', width: 120 },
  { field: 'task_sub_group_count', headerName: '실행과제(건)', width: 120 },
  { field: 'task_count', headerName: '세부실행과제(건)', width: 140 },
  { field: 'task_item_count', headerName: '수행업무(건)', width: 120 },
  { field: 'history_count', headerName: '변경이력 횟수', width: 130 },
  { field: 'postpone_count', headerName: '날짜 연기 횟수', width: 130 },
  { field: 'postpone_days_total', headerName: '날짜 변경일(합계)', width: 150 },
]

const formatCount = (value: unknown): string => {
  if (typeof value !== 'number') return '0'
  return value.toLocaleString()
}

// 상세 보기 — 그리드 context 로 넘긴 onOpenDetail 을 호출한다
const DetailButtonRenderer = (params: ICellRendererParams<StaffWorkloadResponse>) => (
  <div
    onClick={(e) => e.stopPropagation()}
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
  >
    <Button
      size="xsmall"
      color="light"
      onClick={() => params.data && params.context?.onOpenDetail?.(params.data.member_id)}
      className="flex items-center gap-0.5"
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 8px 4px 12px'
      }}
    >
      보기
      <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
    </Button>
  </div>
)

const StaffWorkloadPage: React.FC = () => {
  const currentWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // 상세 패널
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false)
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)
  const gridContext = useMemo(
    () => ({
      onOpenDetail: (memberId: string) => {
        setSelectedMemberId(memberId)
        setIsDetailPanelOpen(true)
      }
    }),
    []
  )
  const handleDetailPanelClose = useCallback(() => {
    setIsDetailPanelOpen(false)
    setSelectedMemberId(null)
  }, [])

  // 검색: 입력값과 실제 적용된 검색어를 분리 (Enter/버튼으로 적용)
  const [searchValue, setSearchValue] = useState('')
  const [appliedQuery, setAppliedQuery] = useState('')

  // 누적 로딩 (변경 이력 화면과 같은 방식)
  const [records, setRecords] = useState<StaffWorkloadResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadedPage, setLoadedPage] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const perPage = 50

  const fetchPage = useCallback(async (targetPage: number) => {
    const orgId = currentWorkspace?.org_id
    const workspaceId = currentWorkspace?.id
    if (!orgId || !workspaceId) {
      setRecords([])
      setTotalItems(0)
      return
    }

    const isReset = targetPage === 1
    if (isReset) setIsLoading(true)
    else setIsLoadingMore(true)
    setError(null)

    try {
      const response = await HistoryStaffWorkloadService.appsHistoryViewsListStaffWorkload({
        orgId,
        workspaceId,
        q: appliedQuery || undefined,
        page: targetPage,
        perPage
      })
      if (response?.data && response.meta) {
        const newItems = response.data
        setTotalItems(response.meta.total || 0)
        setRecords(prev => (isReset ? newItems : [...prev, ...newItems]))
        setLoadedPage(targetPage)
      }
    } catch (err) {
      console.error('Failed to fetch staff workload:', err)
      setError('직원 업무 보유 현황을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [currentWorkspace?.org_id, currentWorkspace?.id, appliedQuery])

  useEffect(() => {
    fetchPage(1)
  }, [fetchPage])

  const loadMore = useCallback(() => {
    if (isLoadingMore || records.length >= totalItems) return
    fetchPage(loadedPage + 1)
  }, [fetchPage, isLoadingMore, loadedPage, records.length, totalItems])

  const handleSearch = useCallback(() => {
    setAppliedQuery(searchValue.trim())
  }, [searchValue])

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
    setAppliedQuery('')
  }, [])

  const columnDefs: ColDef<StaffWorkloadResponse>[] = useMemo(() => [
    {
      headerName: '이메일',
      field: 'account_email',
      width: 220,
      minWidth: 180,
      sortable: false,
      filter: false
    },
    {
      headerName: '사용자명',
      field: 'account_name',
      width: 140,
      minWidth: 120,
      sortable: false,
      filter: false
    },
    ...NUMBER_COLUMNS.map((col): ColDef<StaffWorkloadResponse> => ({
      headerName: col.headerName,
      field: col.field,
      width: col.width,
      minWidth: col.width,
      sortable: false,
      filter: false,
      // 컬럼명은 왼쪽 정렬(기본), 숫자 값만 오른쪽 정렬
      cellClass: 'ag-right-aligned-cell',
      valueFormatter: (params) => formatCount(params.value)
    })),
    {
      headerName: '상세',
      field: 'member_id',
      colId: 'action',
      flex: 1,
      minWidth: 140,
      sortable: false,
      filter: false,
      cellRenderer: DetailButtonRenderer,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' }
    }
  ], [])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar */}
      <div
        className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg"
        style={{ backgroundColor: designTokens.colors.gray[0] }}
      >
        <div className="relative w-[280px] flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
            style={{ backgroundColor: '#FFFFFF', borderColor: designTokens.colors.gray[1] }}
          >
            <Search className="w-5 h-5" style={{ color: designTokens.colors.gray[3] }} />
            <input
              type="text"
              placeholder="이름, 이메일로 검색"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch()
              }}
              className="flex-1 bg-transparent outline-none min-w-0"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: searchValue ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
              }}
            />
          </div>
        </div>
        {appliedQuery && (
          <Button
            size="medium"
            color="white"
            onClick={handleClearSearch}
            className="h-10 px-[10px] flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
          >
            필터 초기화
          </Button>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 mt-6">
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 m-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <DataGrid
            columnDefs={columnDefs}
            rowData={records}
            loading={isLoading}
            className="ag-theme-alpine"
            headerHeight={40}
            rowHeight={42}
            pagination={false}
            height="calc(100vh - 258px)"
            localeText={AG_GRID_LOCALE_KR}
            getRowId={(params: GetRowIdParams<StaffWorkloadResponse>) => params.data.member_id}
            context={gridContext}
            enableRangeSelection={true}
            enableCellCopy={true}
            suppressRowClickSelection={true}
          />

          {/* Footer: 누적 로딩 (더 보기) */}
          <div
            className="flex items-center justify-center gap-4 py-3 border-t"
            style={{ borderTopColor: designTokens.colors.gray[1] }}
          >
            <span
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: designTokens.colors.gray[5]
              }}
            >
              전체 {totalItems.toLocaleString()}명 중 {records.length.toLocaleString()}명 로드
            </span>
            {records.length < totalItems && (
              <Button
                size="small"
                color="white"
                onClick={loadMore}
                disabled={isLoadingMore}
                className="flex-shrink-0"
              >
                {isLoadingMore ? '불러오는 중...' : '더 보기'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <StaffWorkloadDetailPanel
        isOpen={isDetailPanelOpen}
        onClose={handleDetailPanelClose}
        memberId={selectedMemberId}
        orgId={currentWorkspace?.org_id}
        workspaceId={currentWorkspace?.id}
      />
    </div>
  )
}

export default StaffWorkloadPage
